import {
  ProductSubscriptionStatusesEnum,
  RecurringTypesEnum,
  SubscriptionProductNamesEnum,
} from "dash-common";
import { ModalContext, ViewerContext, useScreenResolution } from "dash-ui";
import { useCanUserTrialForProductQuery } from "gql/generated.graphql";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { ISubscriptionPlanCardV3Props } from "./SubscriptionPlanCardV3.interfaces";
import { getProductNameSlug } from "common/helpers/subscriptions";
import { getFeatureLabels } from "pages/ProfilePage/components/ProfileBilling/components/SubscriptionProductsSection/components/ProductCardItem/ProductCardItem.helpers";
import {
  getCardColors,
  getButtonText as getButtonTextHelper,
  getPlanKey,
  getFeaturesSectionTitle as getFeaturesSectionTitleHelper,
} from "./SubscriptionPlanCardV3.helpers";
import CardHeader from "./CardHeader/CardHeader";
import CardActions from "./CardActions/CardActions";
import CardFeatures from "./CardFeatures/CardFeatures";

export default function SubscriptionPlanCardV3({
  product,
  isCurrent,
  onSelectPlan,
  loading,
  className,
  modalId,
  showManageLink,
  onRenewPlan,
}: ISubscriptionPlanCardV3Props) {
  const { t, i18n } = useTranslation(["pages", "common"]);
  const {
    devices: { isMobile },
  } = useScreenResolution();

  const { subscription } = useContext(ViewerContext);
  const { subscriptionProduct: userProduct, status } = subscription || {};
  const isTrialing = status === ProductSubscriptionStatusesEnum.TRIALING;

  const { toggleModal } = useContext(ModalContext);

  // Skip query for mock products (they have mock_ prefix in stripeProductId)
  const isMockProduct = product.stripeProductId?.startsWith("mock_");

  const { data: canUserTrialForProductData } = useCanUserTrialForProductQuery({
    variables: {
      input: {
        stripeProductId: product.stripeProductId || "",
      },
    },
    skip: isMockProduct,
  });
  const canUserTrial =
    canUserTrialForProductData?.canUserTrialForProduct && !isTrialing;

  const gtmProductTag = `gtm-${product?.name
    ?.toLowerCase()
    .split(" ")
    .join("-")}`;

  const productSlug = getProductNameSlug(product);

  const trackingClassName = modalId
    ? `${modalId}-${productSlug}-upgrade-button`
    : `${productSlug}-upgrade-button`;

  const planKey = getPlanKey(product.name);
  const colors = getCardColors(product.name);
  const isBasicTier =
    product.name === SubscriptionProductNamesEnum.BASIC ||
    product.name === SubscriptionProductNamesEnum.DIRECT;
  const isPro =
    product.name === SubscriptionProductNamesEnum.PRO &&
    product.recurringType !== RecurringTypesEnum.SIXMONTH &&
    !product.legacy;
  const isExchange = product.name === SubscriptionProductNamesEnum.EXCHANGE;
  const isDiscontinued = product.recurringType === RecurringTypesEnum.SIXMONTH;

  const featuresList = getFeatureLabels(product, { t, i18n });

  function handleCancelTrial() {
    toggleModal(true, "CancelTrialModal");
  }

  const buttonText = getButtonTextHelper({
    isCurrent,
    isTrialing,
    canUserTrial,
    productTrial: product.trial,
    isBasicTier,
    isExchange,
    isPro,
    productName: product.name,
    t,
  });

  const featuresSectionTitle = getFeaturesSectionTitleHelper(planKey, t);
  const keyFeaturesLabel = t("common:subscriptionProductsKeyFeatures");

  return (
    <div
      className={`relative flex flex-col w-full max-w-[324px] min-w-[320px] rounded-xl shadow-lg overflow-hidden border ${
        isDiscontinued
          ? "bg-gray-100 border-gray-300 opacity-75"
          : "border-gray-200"
      } ${isCurrent ? "ring-4 ring-sky-600 ring-offset-2" : ""} ${className}`}
    >
      <CardHeader
        product={product}
        colors={colors}
        planKey={planKey}
        isPro={isPro}
        isDiscontinued={isDiscontinued}
        t={t}
      />
      <CardActions
        isCurrent={isCurrent}
        isTrialing={isTrialing}
        userProduct={userProduct}
        buttonText={buttonText}
        colors={colors}
        loading={loading}
        subscription={subscription}
        onSelectPlan={onSelectPlan}
        product={product}
        canUserTrial={canUserTrial}
        gtmProductTag={gtmProductTag}
        trackingClassName={trackingClassName}
        isBasicTier={isBasicTier}
        handleCancelTrial={handleCancelTrial}
        showManageLink={showManageLink}
        onRenewPlan={onRenewPlan}
      />
      <CardFeatures
        featuresList={featuresList}
        featuresSectionTitle={featuresSectionTitle}
        keyFeaturesLabel={keyFeaturesLabel}
        isMobile={isMobile}
      />
    </div>
  );
}
